declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[][];
  }
}

export const GA_MEASUREMENT_ID = 'G-5R5VSMX2N0';

type AnalyticsValue = number | string | undefined;
type AnalyticsParams = Record<string, AnalyticsValue>;
type ProcessMode = 'single' | 'batch';

function sendEvent(eventName: string, params: AnalyticsParams = {}) {
  if (!window.gtag) return;

  const safeParams = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined));
  window.gtag('event', eventName, {
    send_to: GA_MEASUREMENT_ID,
    ...safeParams,
  });
}

export function toolNameFromPath(path: string): string {
  return path.replace(/^\//, '').replace(/-/g, '_');
}

export function modeFromCount(count: number): ProcessMode {
  return count > 1 ? 'batch' : 'single';
}

export function trackToolOpened(toolName: string) {
  sendEvent('tool_opened', { tool_name: toolName });
}

export function trackFileAdded(toolName: string, mode: ProcessMode, actionType = 'file_select') {
  sendEvent('file_added', {
    tool_name: toolName,
    mode,
    action_type: actionType,
  });
}

export function trackProcessStarted(toolName: string, mode: ProcessMode, outputType?: string) {
  sendEvent('process_started', {
    tool_name: toolName,
    mode,
    output_type: outputType,
  });
}

export function trackProcessCompleted(toolName: string, mode: ProcessMode, outputType?: string) {
  sendEvent('process_completed', {
    tool_name: toolName,
    mode,
    output_type: outputType,
    result: 'success',
  });
}

export function trackProcessError(toolName: string, mode: ProcessMode, outputType?: string) {
  sendEvent('process_error', {
    tool_name: toolName,
    mode,
    output_type: outputType,
    result: 'error',
  });
}

export function trackDownloadClicked(toolName: string, mode: ProcessMode, outputType: string) {
  sendEvent('download_clicked', {
    tool_name: toolName,
    mode,
    output_type: outputType,
  });
}

export function trackZipDownloadClicked(toolName: string, mode: ProcessMode) {
  sendEvent('zip_download_clicked', {
    tool_name: toolName,
    mode,
    output_type: 'zip',
  });
}

export function trackFeedbackRequestClicked(actionType: 'footer' | 'homepage') {
  sendEvent('feedback_request_clicked', {
    action_type: actionType,
  });
}

export function trackIssueReportClicked(actionType: 'footer' | 'homepage') {
  sendEvent('issue_report_clicked', {
    action_type: actionType,
  });
}

export function trackInstallPromptShown() {
  sendEvent('install_prompt_shown', {
    action_type: 'pwa_install',
  });
}

export function trackInstallClicked() {
  sendEvent('install_clicked', {
    action_type: 'pwa_install',
  });
}

export function trackInstallAccepted() {
  sendEvent('install_accepted', {
    action_type: 'pwa_install',
    result: 'success',
  });
}

export function trackInstallDismissed() {
  sendEvent('install_dismissed', {
    action_type: 'pwa_install',
    result: 'dismissed',
  });
}
